import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { API_URL, ApiResponse } from '../config/api.config';
import { skipErrorToast } from '../helpers/http-context';
import { Attachment, AttachmentOwner } from '../models/attachment.model';

/** Attachments of customers, vendors, purchase orders, sales orders and expenses. */
@Injectable({ providedIn: 'root' })
export class AttachmentService {
  private readonly http = inject(HttpClient);

  list(owner: AttachmentOwner, ownerId: string): Observable<Attachment[]> {
    return this.http
      .get<ApiResponse<Attachment[]>>(`${API_URL}/${owner}/${ownerId}/attachments`)
      .pipe(map((response) => response.data));
  }

  /** Uploads the files in one request. Validation errors are shown by the Attachments section. */
  upload(owner: AttachmentOwner, ownerId: string, files: File[]): Observable<Attachment[]> {
    const body = new FormData();
    files.forEach((file) => body.append('files[]', file, file.name));
    return this.http
      .post<ApiResponse<Attachment[]>>(`${API_URL}/${owner}/${ownerId}/attachments`, body, { context: skipErrorToast() })
      .pipe(map((response) => response.data));
  }

  /** Downloads the file with the session cookie and saves it with its original name. */
  download(attachment: Attachment): void {
    this.http.get(`${API_URL}/attachments/${attachment.id}/download`, { responseType: 'blob' }).subscribe((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
  }

  setValidUntil(attachmentId: string, validUntil: string | null): Observable<Attachment> {
    return this.http
      .patch<ApiResponse<Attachment>>(`${API_URL}/attachments/${attachmentId}`, { validUntil })
      .pipe(map((response) => response.data));
  }

  remove(attachmentId: string): Observable<unknown> {
    return this.http.delete(`${API_URL}/attachments/${attachmentId}`);
  }
}
